import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { obtenerTodasLasMatriculas, actualizarMatricula, limpiarCacheMatriculas, obtenerEstadoCache, limpiarTodoElCache, verificarYLimpiarCacheCorrupto } from '../services/matriculaService';
import CrearMatricula from './Registrar_padre';
import Modal from 'react-modal';
import DetalleMatricula from './detalle_matricula';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FileText, 
  Plus, 
  Users, 
  Calendar,
  X,
  Eye,
  Ban,
  CheckCircle,
  Edit,
  PenSquare,
  Search,
  Filter,
  SlidersHorizontal,
  Edit2
} from 'lucide-react';
import { ToastContainer } from 'react-toastify';

Modal.setAppElement('#root');

// Custom Button Component
const Button = ({ 
  children, 
  onClick, 
  className = '', 
  variant = 'primary', 
  size = 'default' 
}) => {
  const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600',
    danger: 'bg-rose-500 text-white hover:bg-rose-600'
  };

  const sizeStyles = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
    icon: 'p-2',
    xs: 'p-1 text-xs'
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
      `}
    >
      {children}
    </button>
  );
};

const MatriculasPage = () => {
  const [matriculas, setMatriculas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [matriculaSeleccionada, setMatriculaSeleccionada] = useState(null);
  const [modalDetalleIsOpen, setModalDetalleIsOpen] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtroActivo, setFiltroActivo] = useState('busqueda');
  const [paginaActual, setPaginaActual] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [ordenamiento, setOrdenamiento] = useState({ campo: 'fecha_matricula', direccion: 'desc' });
  const matriculasPorPagina = 10;
  
  // Estados para paginación
   const [currentPage, setCurrentPage] = useState(1);
   const [itemsPerPage] = useState(10); // Mostrar 10 elementos por página

  // Filtros
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');

  // Efecto para manejar el cambio de tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar matrículas (OPTIMIZADA PARA CACHE)
  const cargarMatriculas = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      
      
      // Verificar si vamos a usar cache antes de la llamada
      const estadoCache = obtenerEstadoCache();
      const usaraCache = !forceRefresh && estadoCache.matriculas.esValido && estadoCache.matriculas.tieneData;
      
      const response = await obtenerTodasLasMatriculas(forceRefresh);
      
      if (response && response.matriculas && Array.isArray(response.matriculas)) {
        // Filtrar matrículas válidas (sin datos corruptos)
        const matriculasValidas = response.matriculas.filter(m => 
          m.nino && 
          m.matricula && 
          m.nino.nombre && 
          m.nino.nombre !== 'NA' && 
          m.nino.apellidos && 
          m.nino.apellidos !== 'NA' &&
          m.matricula.fecha_matricula &&
          m.matricula.fecha_matricula !== 'Invalid Date'
        );
        
        // Ordenar por fecha más reciente por defecto
        const matriculasOrdenadas = [...matriculasValidas].sort((a, b) => {
          const fechaA = new Date(a.matricula?.fecha_matricula);
          const fechaB = new Date(b.matricula?.fecha_matricula);
          return fechaB - fechaA;
        });
        
        setMatriculas(matriculasOrdenadas);

        
        // Mostrar notificación solo en carga inicial (no en refresh manual)
        if (!forceRefresh && usaraCache) {
          toast.info('📋 Datos cargados desde cache local', {
            position: 'top-right',
            autoClose: 2000,
            style: { marginTop: '10px' }
          });
        } else if (!forceRefresh) {
          toast.success('🌐 Datos cargados desde servidor', {
            position: 'top-right',
            autoClose: 2000,
            style: { marginTop: '10px' }
          });
        }
        
      } else {
        setMatriculas([]);
        if (response && response.message) {
          setError(response.message);
        }
      }
    } catch (err) {
      console.error('Error al cargar matrículas:', err);
      setMatriculas([]);
      setError('Error al cargar las matrículas: ' + err.message);
      
      // Limpiar cache si hay error
      limpiarCacheMatriculas();
      
      toast.error('❌ Error al cargar datos: ' + err.message, {
        position: 'top-right',
        autoClose: 5000,
        style: { marginTop: '10px' }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Función para refrescar datos forzando actualización
  const refrescarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      

      
      // Limpiar todo el cache completamente
      limpiarTodoElCache();
      
      // Limpiar estado local
      setMatriculas([]);
      
      // Pequeña pausa para asegurar que el cache se limpie
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Forzar actualización desde el servidor
      await cargarMatriculas(true);
      
      // Mostrar mensaje de éxito al usuario
      toast.success('✅ Datos actualizados desde el servidor', {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
      
    } catch (error) {
      console.error('Error al refrescar datos:', error);
      setError('Error al refrescar los datos: ' + error.message);
      
      // Mostrar mensaje de error al usuario
      toast.error('❌ Error al actualizar los datos: ' + error.message, {
        position: 'top-right',
        autoClose: 5000,
        style: { marginTop: '10px' }
      });
    }
  }, [cargarMatriculas]);
  
  // Función para obtener información del estado del cache
  const obtenerInfoCache = useCallback(() => {
    const estadoCache = obtenerEstadoCache();
    if (estadoCache.matriculas.esValido && estadoCache.matriculas.tieneData) {
      const tiempoRestante = Math.round((15 * 60 * 1000 - (Date.now() - estadoCache.matriculas.timestamp)) / 60000);
      return {
        usandoCache: true,
        tiempoRestante: Math.max(0, tiempoRestante)
      };
    }
    return { usandoCache: false, tiempoRestante: 0 };
  }, []);

  useEffect(() => {
    // Función para cargar datos iniciales optimizada
    const cargarDatosIniciales = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Verificar si hay datos en cache válidos
        const estadoCache = obtenerEstadoCache();
        
        if (estadoCache.matriculas.esValido && estadoCache.matriculas.tieneData) {
          console.log('Cargando datos desde cache...');
          // Cargar desde cache sin forzar actualización
          const response = await obtenerTodasLasMatriculas(false);
          
          if (response && response.matriculas && Array.isArray(response.matriculas)) {
            // Filtrar matrículas válidas
            const matriculasValidas = response.matriculas.filter(m => 
              m.nino && 
              m.matricula && 
              m.nino.nombre && 
              m.nino.nombre !== 'NA' && 
              m.nino.apellidos && 
              m.nino.apellidos !== 'NA' &&
              m.matricula.fecha_matricula &&
              m.matricula.fecha_matricula !== 'Invalid Date'
            );
            
            // Ordenar por fecha más reciente
            const matriculasOrdenadas = [...matriculasValidas].sort((a, b) => {
              const fechaA = new Date(a.matricula?.fecha_matricula);
              const fechaB = new Date(b.matricula?.fecha_matricula);
              return fechaB - fechaA;
            });
            
            setMatriculas(matriculasOrdenadas);
          } else {
            setMatriculas([]);
          }
        } else {
          console.log('Cache no válido, cargando desde servidor...');
          // Cargar desde servidor
          await cargarMatriculas(true);
        }
      } catch (err) {

        setError('Error al cargar las matrículas: ' + err.message);
        setMatriculas([]);
      } finally {
        setLoading(false);
      }
    };
    
    cargarDatosIniciales();
  }, []); // Solo ejecutar una vez al montar el componente
  
  // Verificar periódicamente si hay datos corruptos (cada 5 minutos para reducir overhead)
  useEffect(() => {
    const interval = setInterval(() => {
      const datosCorruptos = verificarYLimpiarCacheCorrupto();
      if (datosCorruptos) {
        cargarMatriculas(true);
      }
    }, 300000); // 5 minutos (300000 ms)
    
    return () => clearInterval(interval);
  }, [cargarMatriculas]);

  // Usar useMemo para optimizar el filtrado
  const matriculasFiltradas = useMemo(() => {
    let resultado = matriculas;

    if (filtroBusqueda) {
      const busquedaLower = filtroBusqueda.toLowerCase();
      resultado = resultado.filter(m => 
        (m.nino.nombre + ' ' + m.nino.apellidos).toLowerCase().includes(busquedaLower) ||
        m.nino.padre?.nombre?.toLowerCase().includes(busquedaLower)
      );
    }

    if (filtroEstado) {
      resultado = resultado.filter(m =>
        m.matricula?.estado === filtroEstado
      );
    }

    if (filtroFechaInicio && filtroFechaFin) {
      resultado = resultado.filter(m => {
        const fechaMatricula = new Date(m.matricula?.fecha_matricula);
        const inicio = new Date(filtroFechaInicio);
        const fin = new Date(filtroFechaFin);
        return fechaMatricula >= inicio && fechaMatricula <= fin;
      });
    }

    return resultado;
  }, [filtroBusqueda, filtroEstado, filtroFechaInicio, filtroFechaFin, matriculas]);

  const limpiarFiltros = () => {
    setFiltroBusqueda('');
    setFiltroEstado('');
    setFiltroFechaInicio('');
    setFiltroFechaFin('');
    setFiltroActivo('busqueda');
  };

  const renderFiltroActivo = () => {
    switch(filtroActivo) {
      case 'busqueda':
        return (
          <div className="relative">
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
              placeholder="Buscar por nombre del niño o padre..."
              value={filtroBusqueda}
              onChange={(e) => setFiltroBusqueda(e.target.value)}
            />
            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>
        );
      case 'estado':
        return (
          <select
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="Activa">Activa</option>
            <option value="Desactivada">Desactivada</option>
          </select>
        );
      case 'fecha':
        return (
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                type="date"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                value={filtroFechaInicio}
                onChange={(e) => setFiltroFechaInicio(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                type="date"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                value={filtroFechaFin}
                onChange={(e) => setFiltroFechaFin(e.target.value)}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleOpenModal = () => {
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    cargarMatriculas();
  };

  const handleVerDetalle = (matricula) => {
    setMatriculaSeleccionada(matricula);
    setModalDetalleIsOpen(true);
  };

  // Usar useCallback para optimizar las funciones
  const handleDesactivarMatricula = useCallback(async (idChild) => {
    try {
      await actualizarMatricula(idChild, { estado_matricula: 'Desactivada' });
      
      // Actualizar solo el estado local sin recargar toda la página
      setMatriculas(prevMatriculas => 
        prevMatriculas.map(matricula => 
          matricula.nino.id_child === idChild 
            ? { ...matricula, matricula: { ...matricula.matricula, estado: 'Desactivada' } }
            : matricula
        )
      );
      
      toast.success('Matrícula desactivada exitosamente', {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
    } catch (error) {
      console.error('Error al desactivar matrícula:', error);
      toast.error('Error al desactivar la matrícula: ' + error.message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
    }
  }, []);

  const handleActivarMatricula = useCallback(async (idChild) => {
    try {
      await actualizarMatricula(idChild, { estado_matricula: 'Activa' });
      
      // Actualizar solo el estado local sin recargar toda la página
      setMatriculas(prevMatriculas => 
        prevMatriculas.map(matricula => 
          matricula.nino.id_child === idChild 
            ? { ...matricula, matricula: { ...matricula.matricula, estado: 'Activa' } }
            : matricula
        )
      );
      
      toast.success('Matrícula activada exitosamente', {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
    } catch (error) {
      console.error('Error al activar matrícula:', error);
      toast.error('Error al activar la matrícula: ' + error.message, {
        position: 'top-right',
        autoClose: 3000,
        style: { marginTop: '10px' }
      });
    }
  }, []);

  // Función para ordenar las matrículas usando useCallback
  const ordenarMatriculas = useCallback((campo) => {
    // Solo permitir ordenar por los campos especificados
    const camposOrdenables = [
      'matricula.fecha_matricula', // Fecha de matrícula
      'nombreCompleto', // Nombre completo del niño
      'nino.padre.nombre', // Nombre del padre
      'matricula.id_matricula' // Número de matrícula
    ];
    
    if (!camposOrdenables.includes(campo)) {
      return; // No hacer nada si el campo no es ordenable
    }
    
    const esOrdenAscendente = ordenamiento.campo === campo && ordenamiento.direccion === 'asc';
    const nuevaDireccion = esOrdenAscendente ? 'desc' : 'asc';
    
    setOrdenamiento({ campo, direccion: nuevaDireccion });
    
    setMatriculas(prevMatriculas => {
      return [...prevMatriculas].sort((a, b) => {
        let valorA, valorB;
        
        // Manejar campos anidados
        if (campo === 'nino.padre.nombre') {
          // Para el nombre del padre
          valorA = (a.nino?.padre?.nombre || '').toLowerCase();
          valorB = (b.nino?.padre?.nombre || '').toLowerCase();
        } else if (campo.includes('.')) {
          const [objeto, subCampo] = campo.split('.');
          valorA = a[objeto]?.[subCampo] ?? '';
          valorB = b[objeto]?.[subCampo] ?? '';
        } else if (campo === 'nombreCompleto') {
          // Para el nombre completo del niño
          valorA = `${a.nino?.nombre || ''} ${a.nino?.apellidos || ''}`.toLowerCase();
          valorB = `${b.nino?.nombre || ''} ${b.nino?.apellidos || ''}`.toLowerCase();
        } else {
          valorA = a[campo] ?? '';
          valorB = b[campo] ?? '';
        }
        
        // Convertir a string para manejar valores nulos/undefined
        valorA = String(valorA || '');
        valorB = String(valorB || '');
        
        // Manejar fechas (solo para fecha de matrícula)
        if (campo === 'matricula.fecha_matricula') {
          const fechaA = new Date(valorA);
          const fechaB = new Date(valorB);
          return nuevaDireccion === 'asc' ? fechaA - fechaB : fechaB - fechaA;
        }
        
        // Manejar números de matrícula
        if (campo === 'matricula.id_matricula') {
          const numA = parseInt(valorA.replace(/\D/g, ''), 10) || 0;
          const numB = parseInt(valorB.replace(/\D/g, ''), 10) || 0;
          return nuevaDireccion === 'asc' ? numA - numB : numB - numA;
        }
        
        // Ordenamiento alfabético para nombres (niño y padre)
        if (nuevaDireccion === 'asc') {
          return valorA.localeCompare(valorB, 'es', { sensitivity: 'base' });
        } else {
          return valorB.localeCompare(valorA, 'es', { sensitivity: 'base' });
        }
      });
    });
  }, [ordenamiento]);

  // Asegurar que al cargar la página se ordene por fecha más reciente
  useEffect(() => {
    if (matriculas.length > 0 && ordenamiento.campo === 'fecha_matricula') {
      setOrdenamiento({ campo: 'matricula.fecha_matricula', direccion: 'desc' });
    }
  }, [matriculas, ordenamiento.campo]);

  // Calcular índices de paginación optimizada
  const { matriculasPaginaActual, totalPaginas } = useMemo(() => {
    const indiceInicio = (currentPage - 1) * itemsPerPage;
    const indiceFin = indiceInicio + itemsPerPage;
    const paginadas = matriculasFiltradas.slice(indiceInicio, indiceFin);
    const total = Math.ceil(matriculasFiltradas.length / itemsPerPage);
    return { matriculasPaginaActual: paginadas, totalPaginas: total };
  }, [matriculasFiltradas, currentPage, itemsPerPage]);
  
  // Función para renderizar el ícono de ordenamiento
  const renderSortIcon = (campo) => {
    if (ordenamiento.campo !== campo) {
      return <span className="inline-block ml-1 opacity-0">↕</span>;
    }
    return ordenamiento.direccion === 'asc' ? ' ↑' : ' ↓';
  };

  // Función para cambiar de página usando useCallback
  const cambiarPagina = useCallback((numeroPagina) => {
    setCurrentPage(numeroPagina);
    // Scroll hacia arriba al cambiar de página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroBusqueda, filtroEstado, filtroFechaInicio, filtroFechaFin]);

  // Componente de paginación
  const Paginacion = () => {
    const paginasAMostrar = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPaginas, currentPage + 2);

    if (startPage > 1) {
      paginasAMostrar.push(1);
      if (startPage > 2) {
        paginasAMostrar.push('...');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginasAMostrar.push(i);
    }

    if (endPage < totalPaginas) {
      if (endPage < totalPaginas - 1) {
        paginasAMostrar.push('...');
      }
      paginasAMostrar.push(totalPaginas);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <Button
            onClick={() => cambiarPagina(currentPage - 1)}
            disabled={currentPage === 1}
            variant="ghost"
            size="sm"
          >
            Anterior
          </Button>
          <Button
            onClick={() => cambiarPagina(currentPage + 1)}
            disabled={currentPage === totalPaginas}
            variant="ghost"
            size="sm"
          >
            Siguiente
          </Button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, matriculasFiltradas.length)}
              </span>{' '}
              de <span className="font-medium">{matriculasFiltradas.length}</span> resultados
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => cambiarPagina(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <span className="sr-only">Anterior</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              {paginasAMostrar.map((pagina, index) => (
                pagina === '...' ? (
                  <span
                    key={index}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    onClick={() => cambiarPagina(pagina)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      pagina === currentPage
                        ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {pagina}
                  </button>
                )
              ))}
              <button
                onClick={() => cambiarPagina(currentPage + 1)}
                disabled={currentPage === totalPaginas}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
              >
                <span className="sr-only">Siguiente</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-white rounded-lg shadow-lg">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white rounded-2xl p-2 sm:p-4 shadow-lg">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
        style={{ zIndex: 9999 }}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header y Filtros */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="p-3 sm:p-4 md:p-6 border-b border-gray-100">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="w-full sm:w-auto">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800">
                    Matrículas
                  </h1>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">
                    Gestiona las matrículas de los estudiantes
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <button
                    onClick={refrescarDatos}
                    disabled={loading}
                    className="flex items-center justify-center gap-2 text-sm px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={obtenerInfoCache().usandoCache ? 'Forzar actualización desde servidor' : 'Refrescar datos'}
                  >
                    <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {loading ? 'Actualizando...' : (obtenerInfoCache().usandoCache ? 'Actualizar' : 'Refrescar')}
                  </button>
                  <Button
                    onClick={handleOpenModal}
                    variant="primary"
                    size="default"
                    className="flex items-center justify-center gap-2 text-sm w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus size={16} />
                    Registrar Padre/Madre de familia
                  </Button>
                </div>
              </div>

              {/* Filtros */}
              <div className="flex flex-col gap-2">
                {/* Filtros en móvil */}
                <div className="block sm:hidden">
                  <div className="flex flex-col gap-2">
                    <select
                      onChange={(e) => setFiltroActivo(e.target.value)}
                      value={filtroActivo}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm"
                    >
                      <option value="busqueda">Buscar</option>
                      <option value="estado">Estado</option>
                      <option value="fecha">Fecha</option>
                    </select>
                    {filtroActivo && (
                      <div className="w-full">
                        {renderFiltroActivo()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Filtros en desktop */}
                <div className="hidden sm:flex flex-wrap gap-2">
                  <button
                    onClick={() => setFiltroActivo('busqueda')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                      filtroActivo === 'busqueda'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Buscar
                  </button>
                  <button
                    onClick={() => setFiltroActivo('estado')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                      filtroActivo === 'estado'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Estado
                  </button>
                  <button
                    onClick={() => setFiltroActivo('fecha')}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                      filtroActivo === 'fecha'
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Fecha
                  </button>
                </div>

                {filtroActivo && (
                  <div className="hidden sm:block">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        {renderFiltroActivo()}
                      </div>
                      {(filtroBusqueda || filtroEstado || filtroFechaInicio || filtroFechaFin) && (
                        <button
                          onClick={limpiarFiltros}
                          className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 whitespace-nowrap"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Vista móvil de matrículas */}
        <div className="block sm:hidden">
          <div className="space-y-3">
            {matriculasFiltradas.length > 0 ? (
              matriculasFiltradas.map((matricula) => (
                <div 
                  key={matricula.nino.id_child}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
                >
                  <div className="flex flex-col gap-3">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {matricula.nino.nombre} {matricula.nino.apellidos}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {matricula.nino.padre?.nombre || 'No registrado'}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">N° Matrícula:</span>
                        <p className="font-medium">{matricula.matricula?.id_matricula || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Teléfono:</span>
                        <p>{matricula.nino.padre?.telefono || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Fecha:</span>
                        <p>{new Date(matricula.matricula?.fecha_matricula).toLocaleDateString() || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <Button
                        onClick={() => matricula.matricula?.estado === 'Activa' ? 
                          handleDesactivarMatricula(matricula.nino.id_child) : 
                          handleActivarMatricula(matricula.nino.id_child)}
                        variant={matricula.matricula?.estado === 'Activa' ? 'green' : 'danger'}
                        size="sm"
                        className="flex items-center gap-1 text-xs"
                      >
                        {matricula.matricula?.estado === 'Activa' ? (
                          <>
                            <CheckCircle size={12} />
                            Activa
                          </>
                        ) : (
                          <>
                            <X size={12} />
                            Desactivada
                          </>
                        )}
                      </Button>
                      <button
                        onClick={() => handleVerDetalle(matricula)}
                        className="p-2 text-gray-400 hover:text-indigo-500 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col items-center justify-center gap-2 text-center">
                  <Users className="w-8 h-8 text-gray-400" />
                  <p className="text-sm text-gray-500">No hay matrículas registradas</p>
                  <p className="text-xs text-gray-400">Las matrículas registradas aparecerán aquí</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Vista desktop de matrículas */}
        <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden ">
          <div className="relative">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px] sm:w-[120px] cursor-pointer hover:bg-gray-100"
                          onClick={() => ordenarMatriculas('matricula.id_matricula')}
                        >
                          N° Matrícula{renderSortIcon('matricula.id_matricula')}
                        </th>
                        <th 
                          className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px] sm:w-[180px] cursor-pointer hover:bg-gray-100"
                          onClick={() => ordenarMatriculas('nombreCompleto')}
                        >
                          Niño{renderSortIcon('nombreCompleto')}
                        </th>
                        <th 
                          className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px] sm:w-[180px] cursor-pointer hover:bg-gray-100"
                          onClick={() => ordenarMatriculas('nino.padre.nombre')}
                        >
                          REPRESENTANTE{renderSortIcon('nino.padre.nombre')}
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px] sm:w-[140px]">
                          Teléfono
                        </th>
                        <th 
                          className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[120px] sm:w-[140px] cursor-pointer hover:bg-gray-100"
                          onClick={() => ordenarMatriculas('matricula.fecha_matricula')}
                        >
                          Fecha Matrícula{renderSortIcon('matricula.fecha_matricula')}
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px] sm:w-[120px]">
                          Estado
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px] sm:w-[100px]">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {matriculasPaginaActual.length > 0 ? (
                        matriculasPaginaActual.map((matricula) => (
                          <tr key={matricula.nino.id_child} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-600 truncate">
                              {matricula.matricula?.id_matricula || 'N/A'}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 truncate">
                              {matricula.nino.nombre} {matricula.nino.apellidos}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-600 truncate">
                              {matricula.nino.padre?.nombre || 'No registrado'}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-600 truncate">
                              {matricula.nino.padre?.telefono || 'N/A'}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-600 truncate">
                              {new Date(matricula.matricula?.fecha_matricula).toLocaleDateString() || 'N/A'}
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <Button
                                onClick={() => matricula.matricula?.estado === 'Activa' ? 
                                  handleDesactivarMatricula(matricula.nino.id_child) : 
                                  handleActivarMatricula(matricula.nino.id_child)}
                                variant={matricula.matricula?.estado === 'Activa' ? 'green' : 'danger'}
                                size="sm"
                                className="flex items-center gap-1 text-xs whitespace-nowrap"
                              >
                                {matricula.matricula?.estado === 'Activa' ? (
                                  <>
                                    <CheckCircle size={12} />
                                    Activa
                                  </>
                                ) : (
                                  <>
                                    <X size={12} />
                                    Desactivada
                                  </>
                                )}
                              </Button>
                            </td>
                            <td className="px-4 sm:px-6 py-3 sm:py-4">
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleVerDetalle(matricula)}
                                  className="p-1 text-gray-400 hover:text-indigo-500 transition-colors"
                                  title="Ver detalles"
                                >
                                  <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500">
                            <div className="flex flex-col items-center justify-center gap-2">
                              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                              <p className="text-xs sm:text-sm">No hay matrículas registradas</p>
                              <p className="text-xs text-gray-400">Las matrículas registradas aparecerán aquí</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* Paginación solo visible en desktop */}
          {!isMobile && totalPaginas > 1 && <Paginacion />}
        </div>

        {/* Modal de registro */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={handleCloseModal}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          overlayClassName="fixed inset-0 bg-black/40 backdrop-blur-sm"
          contentLabel="Registrar Padre"
          ariaHideApp={false}
        >
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-lg mx-auto">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-medium text-gray-900 flex items-center">
                <Users className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-indigo-500" />
                Registro de Padre
              </h2>
              <Button 
                onClick={handleCloseModal}
                variant="ghost"
                size="icon"
                className="hover:bg-gray-100 rounded-full"
              >
                <X size={18} />
              </Button>
            </div>
            <div className="text-sm text-gray-500 mb-4">
              El padre podrá matricular a sus hijos después de registrarse
            </div>
            <CrearMatricula onClose={handleCloseModal} />
          </div>
        </Modal>

        {/* Modal de detalle */}
        <Modal
          isOpen={modalDetalleIsOpen}
          onRequestClose={() => setModalDetalleIsOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          overlayClassName="fixed inset-0 bg-black/40 backdrop-blur-sm"
          contentLabel="Detalle de Matrícula" 
          ariaHideApp={false}
        >
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-medium text-gray-900">
                Detalle y Edición de Matrícula
              </h2>
              <Button
                onClick={() => setModalDetalleIsOpen(false)}
                variant="ghost"
                size="icon"
                className="hover:bg-gray-100 rounded-full"
              >
                <X size={18} />
              </Button>
            </div>
            {matriculaSeleccionada && (
              <DetalleMatricula 
                matricula={matriculaSeleccionada}
                editable={true}
              />
            )}
          </div>
        </Modal>
        
        {/* Toast Container para notificaciones */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ marginTop: '60px' }}
        />
      </div>
    </div>
  );
};

export default MatriculasPage;